import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Clock, HelpCircle } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosRequest } from "../../../_config/apiClient";

const QuizResult = () => {
    const { attemptId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    /* ================= FETCH RESULT ================= */
    const fetchResult = async () => {
        try {
            const res = await axiosRequest(
                "post",
                "/user/getQuizResult",
                { attemptId },
                { Authorization: `Bearer ${localStorage.getItem("user")}` }
            );

            const { quiz, overall } = res.data;

            const totalSeconds = overall.timeTakenSeconds;

            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;


            const formattedResult = {
                quizName: quiz.name,
                totalQuestions: overall.totalQuestions,
                attempted: overall.attemptedQuestions,
                correct: overall.correctAnswers,
                wrong: overall.wrongAnswers,
                skipped: overall.skipped,
                score: overall.score,
                percentage: overall.percentage,
                timeTaken: {
                    minutes,
                    seconds,
                },

                isPassed: overall.percentage >= 40, // pass criteria
            };

            setResult(formattedResult);
        } catch (err) {
            console.error(err);
            setError("Unable to load exam result. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!attemptId) return navigate("/");
        fetchResult();
    }, [attemptId, navigate]);

    /* ================= LOADING ================= */
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-lg font-semibold">
                Loading your result...
            </div>
        );
    }

    /* ================= ERROR ================= */
    if (error) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-red-600 font-semibold">{error}</p>
                <button
                    onClick={() => navigate("/")}
                    className="px-6 py-2 bg-gray-800 text-white rounded"
                >
                    Go Home
                </button>
            </div>
        );
    }

    /* ================= UI ================= */
    return (
        <div className="min-h-screen bg-gray-100 flex justify-center p-6">
            <div className="w-full max-w-4xl bg-white rounded-xl shadow p-6">

                {/* HEADER */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-blue-700">
                        Exam Result
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {result.quizName}
                    </p>
                </div>

                {/* SUMMARY CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <SummaryCard
                        icon={<CheckCircle className="text-green-600" />}
                        label="Correct"
                        value={result.correct}
                        bg="green"
                    />
                    <SummaryCard
                        icon={<XCircle className="text-red-600" />}
                        label="Wrong"
                        value={result.wrong}
                        bg="red"
                    />
                    <SummaryCard
                        icon={<HelpCircle className="text-yellow-600" />}
                        label="Skipped"
                        value={result.skipped}
                        bg="yellow"
                    />
                    <SummaryCard
                        icon={<Clock className="text-blue-600" />}
                        label="Time (min)"
                        value={`${result.timeTaken.minutes}m ${result.timeTaken.seconds}s`}
                        bg="blue"
                    />
                </div>

                {/* SCORE */}
                <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-gray-800">
                        {result.score} / {result.totalQuestions}
                    </div>
                    <div className="text-gray-600">
                        Percentage: {result.percentage}%
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                        Attempted: {result.attempted} / {result.totalQuestions}
                    </div>
                </div>

                {/* PASS / FAIL */}
                <div
                    className={`text-center text-lg font-semibold mb-8 ${result.isPassed ? "text-green-600" : "text-red-600"
                        }`}
                >
                    {result.isPassed ? "🎉 PASSED" : "❌ FAILED"}
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex justify-center gap-4">
                    <button
                        onClick={() => navigate("/dashboard")}
                        className="px-6 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
                    >
                        Go Dashboard
                    </button>

                    {result.attempted > 0 && (
                        <button
                            onClick={() => navigate(`/dashboard/free-quizzes`)}
                            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        >
                            Quiz Page
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ================= REUSABLE CARD ================= */
const SummaryCard = ({ icon, label, value, bg }) => {
    const bgMap = {
        green: "bg-green-50 border-green-300",
        red: "bg-red-50 border-red-300",
        yellow: "bg-yellow-50 border-yellow-300",
        blue: "bg-blue-50 border-blue-300",
    };

    return (
        <div
            className={`border rounded-lg p-4 text-center ${bgMap[bg]}`}
        >
            <div className="flex justify-center mb-2">{icon}</div>
            <div className="text-xl font-bold">{value}</div>
            <div className="text-sm text-gray-600">{label}</div>
        </div>
    );
};

export default QuizResult;
